/*
 * The envelope this app wraps around every expiring Web Storage item, the rule that tells expiring keys from permanent
 * ones, and the sweep that removes expired items. Free of DOM types so it can be unit tested; src/static/lib/util/storageUtil.ts
 * wires it to localStorage and sessionStorage.
 */
import { getClLogger } from '../util/clLogger';

const clLogger = getClLogger(__filename);

// Every key this app writes to localStorage or sessionStorage starts with this. Keys without it belong to other code on the
// same origin (msal-browser account entities, plotly, another app served from localhost) and are never read or removed.
export const CACHE_STORAGE_PREFIX = 'CL:';
const VALUE_PREVIEW_LENGTH = 60;

export interface IExpiringEnvelope {
    cacheMaxTime: number;
    createdTimestamp: number;
}

// What setCacheKey writes for an expiring key, and the only shape fetchCacheKey accepts back for one.
export interface ICachedItem<T> extends IExpiringEnvelope {
    value: T;
}

// The part of the Web Storage API the sweep uses, so an in-memory double can stand in for localStorage in tests.
export interface IStorageLike {
    readonly length: number;
    key(index: number): string | null;
    getItem(key: string): string | null;
    removeItem(key: string): void;
}

// Stored names of the keys the app writes without an envelope, by where the registered name sits in the stored key.
// The user* lists apply to user-scoped keys, which are stored as `CL:<user id>:<name>`, and match on the name alone.
export interface IPermanentKeyNames {
    names: string[];
    prefixes: string[];
    suffixes: string[];
    userNames: string[];
    userPrefixes: string[];
    userSuffixes: string[];
    userMiddles: string[];
}

export interface IParsedItem {
    readable: boolean;
    item?: unknown;
    error?: Error;
}

export interface ISweepReport {
    // Expired envelopes that were removed.
    removed: string[];
    // Items under an expiring key that were not envelopes at all (not JSON, or JSON of another shape); also removed.
    unexpected: string[];
    // Keys the sweep could not classify, read or remove; left as they were.
    failed: string[];
}

export function isExpiringEnvelope(item: unknown): item is IExpiringEnvelope {
    const envelope = item as IExpiringEnvelope;
    return typeof envelope === 'object' && envelope !== null && typeof envelope.createdTimestamp === 'number' && typeof envelope.cacheMaxTime === 'number';
}

// Live means stamped with a positive max age that has not run out. Anything else, including items that are not envelopes, counts as expired.
export function isLiveEnvelope(item: unknown, now: number = Date.now()): item is IExpiringEnvelope {
    return isExpiringEnvelope(item) && item.createdTimestamp > 0 && item.cacheMaxTime > 0 && now - item.createdTimestamp <= item.cacheMaxTime;
}

export function parseStoredItem(raw: string | null): IParsedItem {
    if (raw === null) {
        return { readable: false };
    }
    try {
        return { readable: true, item: JSON.parse(raw) };
    }
    catch (error) {
        return { readable: false, error };
    }
}

// A single-line preview of a stored value for log lines, so an unexpected item can be recognised without dumping it whole.
export function describeStoredValue(raw: string | null): string {
    if (raw === null) {
        return 'null';
    }
    const oneLine = raw.replace(/\s+/g, ' ');
    return oneLine.length > VALUE_PREVIEW_LENGTH ? `${oneLine.slice(0, VALUE_PREVIEW_LENGTH)}… (${raw.length} chars)` : oneLine;
}

// Builds the test that tells an expiring key (true) from a permanent one (false). User-scoped keys are classified by their
// name whoever the user is, so another user's preferences, or the current user's before login, are never taken for expired cache.
export function createExpiringKeyTest(permanent: IPermanentKeyNames): (storedKey: string) => boolean {
    return (storedKey) => {
        const key = storedKey.startsWith(CACHE_STORAGE_PREFIX) ? storedKey.slice(CACHE_STORAGE_PREFIX.length) : storedKey;
        if (permanent.names.includes(key) || permanent.prefixes.some((prefix) => key.startsWith(prefix)) || permanent.suffixes.some((suffix) => key.endsWith(suffix))) {
            return false;
        }
        const userSeparator = key.indexOf(':');
        if (userSeparator > 0) {
            const name = key.slice(userSeparator + 1);
            if (permanent.userNames.includes(name) || permanent.userPrefixes.some((prefix) => name.startsWith(prefix)) ||
                permanent.userSuffixes.some((suffix) => name.endsWith(suffix)) ||
                permanent.userMiddles.some((middle) => name.startsWith(middle) || name.includes(`:${middle}`))) {
                return false;
            }
        }
        return true;
    };
}

// Removes this app's expired items from one storage. Each key is handled on its own: an item that cannot be classified, read,
// parsed or removed is logged with its key and a preview of its value, and the sweep carries on with the next key.
export function sweepExpiredItems(storage: IStorageLike, label: string, isExpiringKey: (storedKey: string) => boolean, now: number = Date.now()): ISweepReport {
    const report: ISweepReport = { removed: [], unexpected: [], failed: [] };
    // Snapshot the keys first: removing while iterating by index skips entries.
    const keys: string[] = [];
    try {
        for (let index = 0; index < storage.length; index++) {
            const key = storage.key(index);
            if (key?.startsWith(CACHE_STORAGE_PREFIX)) {
                keys.push(key);
            }
        }
    }
    catch (error) {
        clLogger.error(`Could not list ${label} keys; skipping this sweep`, error);
        return report;
    }
    for (const key of keys) {
        let raw: string | null;
        try {
            if (!isExpiringKey(key)) {
                continue;
            }
            raw = storage.getItem(key);
        }
        catch (error) {
            report.failed.push(key);
            clLogger.error(`Could not read ${label} item ${key}; leaving it alone`, error);
            continue;
        }
        const parsed = parseStoredItem(raw);
        if (isLiveEnvelope(parsed.item, now)) {
            continue;
        }
        const envelope = parsed.readable && isExpiringEnvelope(parsed.item);
        const reason = envelope ? 'expired' : parsed.readable ? 'not a cache envelope' : `not JSON (${parsed.error?.message ?? 'empty'})`;
        try {
            storage.removeItem(key);
            if (envelope) {
                report.removed.push(key);
            }
            else {
                report.unexpected.push(key);
                clLogger.warn(`Removed ${label} item ${key}: ${reason}. Value was: ${describeStoredValue(raw)}`);
            }
        }
        catch (error) {
            report.failed.push(key);
            clLogger.error(`Could not remove ${label} item ${key} (${reason}). Value is: ${describeStoredValue(raw)}`, error);
        }
    }
    return report;
}
